/**
 * Local Wikipedia-style order lore articles, keyed by order id.
 * Display names and images come from the API (order_translations + /images/{id}.jpg).
 * Order IDs are merge keys only — never shown in the UI.
 *
 * To author an article later, add an entry to ORDER_LORE_BY_ID:
 *   64: { orderId: 64, format: 'wiki', text: `**Panicked Roolac** is ...` },
 */

export const DEFAULT_ORDER_LORE_TEXT = 'Coming soon.';

/**
 * Authored order lore by numeric order id.
 * Unlisted orders still appear in Lore and use DEFAULT_ORDER_LORE_TEXT.
 * @type {Record<number|string, { orderId: number, format?: string, text: string }>}
 */
export const ORDER_LORE_BY_ID = {
  // Intentionally empty until order articles are authored.
};

/**
 * Build the Lore orders section from API order rows + local texts.
 * @param {Array<{ id?: string|number, orderId: number, title: string, image?: string, text?: string }>} apiOrders
 * @returns {Array}
 */
export function mergeOrderLoreArticles(apiOrders) {
  if (!Array.isArray(apiOrders)) {
    throw new Error('mergeOrderLoreArticles: apiOrders array is required');
  }

  return apiOrders.map((entry) => {
    if (entry == null || entry.orderId == null || entry.orderId === '') {
      throw new Error(
        `mergeOrderLoreArticles: orderId is required (title=${entry && entry.title})`
      );
    }
    if (!entry.title) {
      throw new Error(`mergeOrderLoreArticles: title is required for orderId=${entry.orderId}`);
    }

    const orderId = Number(entry.orderId);
    if (!Number.isFinite(orderId)) {
      throw new Error(`mergeOrderLoreArticles: invalid orderId=${entry.orderId}`);
    }

    const local = ORDER_LORE_BY_ID[orderId] || ORDER_LORE_BY_ID[String(orderId)];
    if (local && local.text == null) {
      throw new Error(`mergeOrderLoreArticles: incomplete local article for orderId=${orderId}`);
    }

    return {
      id: entry.id != null ? entry.id : `order-${orderId}`,
      orderId,
      title: entry.title,
      image: entry.image || `/images/${orderId}.jpg`,
      text: (local && local.text) || DEFAULT_ORDER_LORE_TEXT,
      format: (local && local.format) || 'wiki',
    };
  });
}
